import type { ComponentType } from 'react'
import { View, Text } from 'react-native'
import { ScrollView } from 'react-native-gesture-handler'
import { Calendar, Globe, Landmark, Map, MapPin } from '@/components/ui/icons'
import { Pressable } from '@/components/ui/Pressable'
import { selection } from '@/lib/utils/haptics'
import { useTranslation } from 'react-i18next'
import { useTheme } from '@/lib/hooks/use-theme'
import { useSemanticColors, RADIUS, SPACING } from '@/lib/theme/tokens'
import { WEB_SHADOWS } from '@/lib/theme/web-shadow'
import { COLORS, FONTS } from '@/lib/brand'
import { NATIONAL_THEME_CATEGORY_CODES } from '@/lib/tours/catalog-layout'

type IconComponent = ComponentType<{
  size?: number
  color?: string
  strokeWidth?: number
}>

const CHIP_HEIGHT = 50
const DESKTOP_CHIP_HEIGHT = 46
const ACTIVE_BACKGROUND = COLORS.purple.DEFAULT

function hexToRgba(hex: string, alpha: number) {
  const normalized = hex.replace('#', '')
  if (!/^[0-9A-Fa-f]{6}$/.test(normalized)) return hex
  const red = parseInt(normalized.slice(0, 2), 16)
  const green = parseInt(normalized.slice(2, 4), 16)
  const blue = parseInt(normalized.slice(4, 6), 16)
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`
}

export interface TourScopeCategory {
  code: string
  name: string
}

interface TourScopeFilterProps {
  selected: string
  onSelect: (next: string) => void
  hasRegional: boolean
  categories: TourScopeCategory[]
  /** Wrapping chips on desktop/tablet so nothing hides off-screen. */
  desktop?: boolean
  /** Horizontal inset. False when the parent already pads the page. */
  padded?: boolean
}

/**
 * Misma pastilla que Explore: 50pt, radio lg, púrpura lleno al seleccionar.
 * Oriente Mágico y Ruta del Pacífico no entran acá — son nacionales.
 */
export function TourScopeFilter({
  selected,
  onSelect,
  hasRegional,
  categories,
  desktop = false,
  padded = true,
}: TourScopeFilterProps) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const inactiveBorderAlpha = isDark ? 0.36 : 0.14
  const inactiveChipBackground = isDark ? colors.surface : COLORS.white

  function renderChip({
    id,
    label,
    Icon,
    active,
    accentColor,
    onPress,
    testID,
  }: {
    id: string
    label: string
    Icon: IconComponent
    active: boolean
    accentColor: string
    onPress: () => void
    testID: string
  }) {
    const borderColor = active
      ? ACTIVE_BACKGROUND
      : isDark
        ? hexToRgba(COLORS.white, inactiveBorderAlpha)
        : hexToRgba(accentColor, inactiveBorderAlpha)

    return (
      <Pressable
        key={id}
        onPress={() => {
          void selection()
          onPress()
        }}
        accessibilityRole="button"
        accessibilityState={{ selected: active }}
        accessibilityLabel={label}
        testID={testID}
        style={({ pressed }) => ({
          borderRadius: RADIUS.lg,
          overflow: 'visible',
          opacity: pressed ? 0.9 : 1,
        })}
      >
        <View
          style={{
            minHeight: desktop ? DESKTOP_CHIP_HEIGHT : CHIP_HEIGHT,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: SPACING.sm,
            paddingHorizontal: desktop ? SPACING.md : 13,
            borderRadius: RADIUS.lg,
            borderWidth: 1,
            borderColor,
            backgroundColor: active ? ACTIVE_BACKGROUND : inactiveChipBackground,
            ...(active ? WEB_SHADOWS.sm : {}),
          }}
        >
          <Icon size={22} color={active ? COLORS.white : accentColor} strokeWidth={2.1} />
          <Text
            numberOfLines={1}
            adjustsFontSizeToFit
            minimumFontScale={0.88}
            style={{
              fontSize: desktop ? 13 : 14,
              lineHeight: desktop ? 18 : 19,
              fontFamily: active ? FONTS.bodyBold : FONTS.body,
              color: active ? COLORS.white : colors.textPrimary,
            }}
          >
            {label}
          </Text>
        </View>
      </Pressable>
    )
  }

  const categoryIcon = (code: string): { Icon: IconComponent; accent: string } => {
    if (code === 'viajes-ejecutivos') return { Icon: Landmark, accent: COLORS.purple.DEFAULT }
    if (code === 'eventos') return { Icon: Calendar, accent: COLORS.coral.DEFAULT }
    return { Icon: Map, accent: COLORS.purple.DEFAULT }
  }

  const chips = [
    renderChip({
      id: 'all',
      label: t('tours.all'),
      Icon: Map,
      active: selected === 'all',
      accentColor: COLORS.purple.DEFAULT,
      onPress: () => onSelect('all'),
      testID: 'tours.category-all',
    }),
    renderChip({
      id: 'national',
      label: t('tours.scope_national'),
      Icon: MapPin,
      active: selected === 'national',
      accentColor: COLORS.purple.DEFAULT,
      onPress: () => onSelect(selected === 'national' ? 'all' : 'national'),
      testID: 'tours.category-national',
    }),
    hasRegional
      ? renderChip({
          id: 'regional',
          label: t('tours.scope_regional'),
          Icon: Globe,
          active: selected === 'regional',
          accentColor: COLORS.purple.DEFAULT,
          onPress: () => onSelect(selected === 'regional' ? 'all' : 'regional'),
          testID: 'tours.category-regional',
        })
      : null,
    ...categories
      .filter((category) => !NATIONAL_THEME_CATEGORY_CODES.has(category.code))
      .map((category) => {
        const { Icon, accent } = categoryIcon(category.code)
        const active = selected === category.code
        return renderChip({
          id: category.code,
          label: category.name,
          Icon,
          active,
          accentColor: accent,
          onPress: () => onSelect(active ? 'all' : category.code),
          testID: `tours.category-${category.code}`,
        })
      }),
  ]

  if (desktop) {
    return (
      <View
        style={{
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: SPACING.sm,
          paddingVertical: SPACING.xs,
          paddingHorizontal: padded ? SPACING.lg : 0,
        }}
      >
        {chips}
      </View>
    )
  }

  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      style={{ flexGrow: 0 }}
      contentContainerStyle={{
        gap: SPACING.sm,
        paddingVertical: SPACING.xs,
        paddingHorizontal: padded ? SPACING.lg : 0,
      }}
    >
      {chips}
    </ScrollView>
  )
}
